import {
  extraGuestCapacity,
  resolveExtraGuestPrice,
  resolveTransportPrice,
} from "@majourneys/shared";
import type { GuestsValue, PackageInfo } from "./types";

/** Transport tier resolver + constants live in `@majourneys/shared` (single
 * source of truth across api/web/admin/mobile). Re-exported here for back-compat. */
export {
  allowsExtraGuests,
  extraGuestCapacity,
  extraGuestCount,
  includedGuestsPerUnit,
  resolveExtraGuestPrice,
  LARGE_GROUP_MIN_GUESTS,
  LARGE_GROUP_MAX_GUESTS,
  hasOutsideSurcharge,
  isTransportAvailable,
  isTransportOffered,
  resolveTransportPrice,
} from "@majourneys/shared";

/** Whole-day nights between two ISO dates, minimum 1, NaN-safe. */
export function nightsBetween(start: string | null, end: string | null): number {
  if (!start || !end) return 1;
  const s = new Date(start);
  const e = new Date(end);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return 1;
  return Math.max(1, Math.round((e.getTime() - s.getTime()) / 86400000));
}

/** Negative or null price = category not available; treat as 0 contribution. */
export function priceOrZero(p: number | null | undefined): number {
  return typeof p === "number" && p > 0 ? p : 0;
}

/**
 * Headcount ceiling for a (package, group count) pair.
 *
 * On per-group pricing (typePrice 3) a package's `maxGuests` describes the
 * capacity of ONE group — one quad seats 2, one boat seats 8 — so the total
 * headcount a booking can carry is `maxGuests × groups`. Capping at `maxGuests`
 * alone makes a party of 4 unbookable on a 2-seat quad no matter how many
 * quads they take.
 *
 * Returns 0 when the package declares no usable cap; callers treat that as
 * "uncapped" and fall back to their own ceiling.
 */
/**
 * Every vehicle on a per-unit booking needs somebody to ride it. Booking two
 * quads for one adult is not a real order: MA-1788433484879 arrived as 1 adult
 * / 2 quads / 800 MAD and nobody could have driven the second machine. Children
 * do not count, they cannot take the controls.
 *
 * Returns the adult floor for a given vehicle count. `groups` is 0 or 1 on
 * everything that is not per-vehicle, so this is a no-op there.
 */
export function minAdultsForGroups(groups: number | null | undefined): number {
  return Math.max(1, groups ?? 1);
}

export function guestCapacity(
  typePrice: 0 | 1 | 2 | 3,
  maxGuests: number | null | undefined,
  groups: number,
  pkg?: Pick<PackageInfo, "includedGuests"> & { pricing?: { extraGuest?: number | null } },
): number {
  if (typeof maxGuests !== "number" || maxGuests <= 0) return 0;
  // A stay's `maxGuests` is one tent or lodge: two lodges sleep twice as many
  // (`groups` carries the units booked; 1 everywhere a stay books one).
  if (typePrice === 2) return maxGuests * Math.max(1, Math.floor(groups) || 1);
  if (typePrice !== 3) return maxGuests;
  // Per-vehicle packages that price a shared seat open the full machine;
  // those that don't cap at what the fare covers, so a second rider can't
  // slip on for free. Callers that pass no package keep the old ceiling.
  return (
    extraGuestCapacity(
      {
        maxGuests,
        includedGuests: pkg?.includedGuests,
        extraGuestPrice: pkg?.pricing?.extraGuest,
      },
      groups,
    ) ?? 0
  );
}

/**
 * Booking total for a single (package, selections) tuple.
 * typePrice: 0 = per-person, 1 = per-hour, 2 = per-night (stays), 3 = per-group flat.
 * `nights` is ignored when typePrice !== 2. `groups` is the units booked: the
 * vehicles on per-group pricing, the tents or lodges on a stay. `transportOutside` adds the
 * package's outside-city pickup surcharge; it is ignored without `transport`.
 */
export function calcTotal(
  typePrice: 0 | 1 | 2 | 3,
  pkg: PackageInfo,
  guests: GuestsValue,
  hours: number,
  transport: boolean,
  nights: number,
  groups = 1,
  transportOutside = false,
): number {
  let base = 0;
  if (typePrice === 0) {
    const adultCount = pkg.pricing.adult === -1 ? 0 : guests.adults;
    const childCount = pkg.pricing.child === -1 ? 0 : guests.children;
    const infantCount = pkg.pricing.infant === -1 ? 0 : guests.infants;
    base =
      adultCount * priceOrZero(pkg.pricing.adult) +
      childCount * priceOrZero(pkg.pricing.child) +
      infantCount * priceOrZero(pkg.pricing.infant);
  } else if (typePrice === 1) {
    base = hours * priceOrZero(pkg.pricing.adult);
  } else if (typePrice === 2) {
    // Per tent or lodge per night: four friends in two lodges pay for two.
    base = nights * priceOrZero(pkg.pricing.adult) * Math.max(1, groups);
  } else {
    base = Math.max(1, groups) * priceOrZero(pkg.pricing.adult);
    // Riders sharing a unit beyond what its flat rate covers (a second person
    // on a 300cc quad). 0 when the package does not publish a sharing price.
    base += resolveExtraGuestPrice(
      {
        maxGuests: pkg.maxGuests,
        includedGuests: pkg.includedGuests,
        extraGuestPrice: pkg.pricing.extraGuest,
      },
      guests.adults + guests.children + guests.infants,
      Math.max(1, groups),
    );
  }
  if (transport) {
    const paidGuests = guests.adults + guests.children + guests.infants;
    base += resolveTransportPrice(
      pkg.pricing.transport,
      pkg.pricing.transportLarge ?? null,
      paidGuests,
      {
        outside: transportOutside,
        outsideSurcharge: pkg.pricing.transportOutside ?? null,
      },
    );
  }
  return base;
}

/**
 * The package with every price converted by `unit` (one rounded price in the
 * guest's currency). `calcTotal` over the result is the sum of the rounded
 * prices the page shows, so the lines and the total agree. Display only: the
 * checkout gets `calcTotal` over the dirham package.
 */
export function pricedIn<P extends { pricing: object }>(pkg: P, unit: (mad: number) => number): P {
  const pricing = Object.fromEntries(
    Object.entries(pkg.pricing).map(([k, v]) => [k, typeof v === "number" ? unit(v) : v]),
  );
  return { ...pkg, pricing } as P;
}

/** One line that is quantity x a single price: add-ons, treatments. */
export type PricedLine = { lineTotal: number; quantity: number };

/** A line's amount as shown: quantity x its rounded unit price. */
export function shownLine(line: PricedLine, unit: (mad: number) => number): number {
  return line.quantity > 0 ? unit(line.lineTotal / line.quantity) * line.quantity : 0;
}

/**
 * The total a page shows: every price converted and rounded first, then
 * added up, so it equals the sum of the lines on screen (owner, 2026-09-29).
 * Mirrors how the dirham total is built (calcTotal, or the treatment lines,
 * plus add-ons, minus the coupon). Display only.
 */
export function shownTotal(
  o: {
    typePrice: 0 | 1 | 2 | 3;
    pkg: Parameters<typeof calcTotal>[1];
    guests: Parameters<typeof calcTotal>[2];
    hours: number;
    transport: boolean;
    nights: number;
    groups?: number;
    transportOutside?: boolean;
    packLines?: PricedLine[] | null;
    addons?: PricedLine[];
    discount?: number;
  },
  unit: (mad: number) => number,
): number {
  const base = o.packLines
    ? o.packLines.reduce((sum, line) => sum + shownLine(line, unit), 0)
    : calcTotal(o.typePrice, pricedIn(o.pkg, unit), o.guests, o.hours, o.transport, o.nights, o.groups, o.transportOutside);
  const addons = (o.addons ?? []).reduce((sum, line) => sum + shownLine(line, unit), 0);
  return Math.max(0, base + addons - unit(o.discount ?? 0));
}

/** A converted amount that keeps its sign: `unit` passes negatives through. */
function signedUnit(mad: number, unit: (mad: number) => number): number {
  return mad < 0 ? -unit(-mad) : unit(mad);
}

/**
 * The total a SAVED booking shows (thank-you page, basket companions): every
 * line the page prints converted and rounded on its own, then added up, so
 * "2 x 72 EUR" sits over 144 and not the 143 that converting 1520 MAD in one
 * go gives (MA-1791540339462, Marajah Spa, 2026-10). Whatever the total holds
 * that the page does not itemise (the base price of a single package) is
 * converted as one more line. Display only: the booking stays in dirhams.
 */
export function shownBookedTotal(
  b: {
    total: number;
    transportPrice?: number;
    discount?: number;
    packs?: Array<{ unitPrice: number; quantity: number; lineTotal: number }> | null;
    addons?: PricedLine[] | null;
  },
  unit: (mad: number) => number,
): number {
  const packs = b.packs ?? [];
  const addons = b.addons ?? [];
  const transport = b.transportPrice ?? 0;
  const discount = b.discount ?? 0;
  const packsMad = packs.reduce((sum, line) => sum + line.lineTotal, 0);
  const addonsMad = addons.reduce((sum, line) => sum + line.lineTotal, 0);
  const restMad = b.total - packsMad - addonsMad - transport + discount;
  const shown =
    packs.reduce((sum, line) => sum + (line.quantity > 0 ? unit(line.unitPrice) * line.quantity : 0), 0) +
    signedUnit(restMad, unit) +
    addons.reduce((sum, line) => sum + shownLine(line, unit), 0) +
    unit(transport) -
    unit(discount);
  return Math.max(0, shown);
}
